import { useCallback, useEffect, useState } from 'react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    ApiCallError, getDoorText, sendDoorText,
    getAppointmentDocuments, getSignPreview, signDocumentApi, getDocumentUrl,
    type DocumentDto, type DoorTextKind,
} from '@/api/client';
import { parsePlacement } from '@/lib/utils';
import type { DoorTextDto } from '@/api/types';

/**
 * TEXT A WAITING DRIVER HIS DOCK DOOR.
 *
 * ⭐ THE FLOW THIS SERVES (Pete, 2026-08-19). The driver checks in, his licence
 * is scanned and his paperwork taken, and then either we can tell him his door
 * or we cannot. If we cannot, he waits in the lot — and this is how he finds
 * out, without anybody walking out to the truck.
 *
 * ⛔ THIS IS NOT SEND DIRECTIONS. Directions answer "how do I get to you" and go
 * out before he arrives. This answers "where do I back in" to a man already in
 * the yard with the engine running. Same phone, different question, different
 * moment — and putting a map link in this one would bury the door.
 *
 * ⚠️ THE PREVIEW IS THE SERVER'S MESSAGE, fetched, not rebuilt here. A UI that
 * composed its own would eventually show one thing and send another.
 */

interface Props {
    open: boolean;
    appointmentId: string | null;
    /**
     * 'door' — where to back in, sent to a driver waiting in the lot.
     * 'paperwork' — his truck is finished, come and collect the signed papers.
     *
     * ⭐ ONE DIALOG, TWO MESSAGES. The recipients, the consent gate, the dedup
     * key and the audit line are identical; only the words differ. A second
     * dialog would be a second place to forget the substitute-driver label.
     */
    /** ⭐ 'work' added #302 — "unloading/loading has started". */
    kind?: DoorTextKind;
    /**
     * ⛔ NEEDED FOR THE AUTOMATIC STAMP (#146), and it must be the RIGHT carrier.
     * `lastPlacementFor` falls back to *any* placement at the facility when the
     * carrier is null — which is another carrier's page layout, and #110 exists
     * precisely because "the docs will be different for each carrier". Passing it
     * is what keeps a remembered position tied to the paperwork it was learned on.
     */
    carrierCode?: string | null;
    facilityCode?: string | null;
    getToken: () => Promise<string>;
    onClose: () => void;
    onSent?: (summary: { sent: number; optedOut: number; failed: number; doorCode: string | null }) => void;
}

export function DoorTextDialog({
    open, appointmentId, kind = 'door', carrierCode, facilityCode,
    getToken, onClose, onSent,
}: Props) {
    const [details, setDetails] = useState<DoorTextDto | null>(null);
    const [chosen, setChosen] = useState<Set<string>>(new Set());
    const [note, setNote] = useState('');
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // ---- #146: sign + timestamp + print, from this dialog ------------------
    // Pete: *"When we tell or text the driver his paperwork is ready, should it
    // Symbia sign and timestamp and print it all at that time? Maybe have those
    // checkboxes at the bottom of the 'Tell the driver his Paperwork is Ready'
    // pop-up?"* — yes, with one hard constraint, below.
    const [unsigned, setUnsigned] = useState<DocumentDto[] | null>(null);
    /** The remembered stamp position for THIS carrier, or null if there is none. */
    const [placement, setPlacement] = useState<string | null>(null);
    const [signEnabled, setSignEnabled] = useState(false);
    const [autoSign, setAutoSign] = useState(false);
    const [autoPrint, setAutoPrint] = useState(false);
    /** Set when the text went but the stamp did not — two different outcomes. */
    const [postSend, setPostSend] = useState<string | null>(null);

    const load = useCallback(async (withNote: string) => {
        if (!appointmentId) return;
        setLoading(true);
        setError(null);
        try {
            const res = await getDoorText(await getToken(), appointmentId, withNote || null, kind);
            setDetails(res);
            // ⭐ PRE-TICK THE MAN WHO ACTUALLY TURNED UP, and the booked driver
            // when they are the same person. A scheduler doing this thirty times
            // a morning should not have to tick a box each time — but the choice
            // is still visible and still theirs.
            setChosen(prev => (prev.size > 0 ? prev : new Set(
                res.recipients
                    .filter(r => r.source === 'checkin' || r.alsoBooked || res.recipients.length === 1)
                    .map(r => r.phone),
            )));
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'Could not read this appointment.');
        } finally {
            setLoading(false);
        }
    }, [appointmentId, getToken, kind]);

    useEffect(() => {
        if (!open) return;
        setDetails(null);
        setChosen(new Set());
        setNote('');
        setUnsigned(null);
        setPlacement(null);
        setSignEnabled(false);
        setAutoSign(false);
        setAutoPrint(false);
        setPostSend(null);
        void load('');
    }, [open, load]);

    /**
     * What is there to sign, and do we know where the stamp goes?
     *
     * ⛔ PAPERWORK ONLY. There is nothing to sign when the message is "your door is
     * 12" — the truck has not been unloaded yet.
     */
    useEffect(() => {
        if (!open || kind !== 'paperwork' || !appointmentId || !facilityCode) return;
        let cancelled = false;
        (async () => {
            try {
                const token = await getToken();
                const { documents } = await getAppointmentDocuments(token, appointmentId);
                const live = documents.filter(d => !d.voidedUtc && !d.purged);
                // An ORIGINAL with no signed copy of its own. Signing a signed copy
                // would stamp a stamp (see AppointmentDocumentsDialog).
                const todo = live.filter(d =>
                    !d.signedFromDocumentId
                    && !live.some(x => x.signedFromDocumentId === d.documentId));
                if (cancelled) return;
                setUnsigned(todo);
                if (todo.length === 0) return;

                const pv = await getSignPreview(token, todo[0].documentId, facilityCode, {
                    carrier: carrierCode ?? null, appointment: appointmentId,
                });
                if (cancelled) return;
                setSignEnabled(pv.enabled);
                setPlacement(pv.lastPlacement);
            } catch {
                // ⚠️ SILENT, AND THE CHECKBOX SIMPLY STAYS OFF. This is an optional
                // extra on a dialog whose job is texting a driver; failing to work
                // out whether we could also stamp something must not present itself
                // as a problem with the text.
                if (!cancelled) { setUnsigned([]); setSignEnabled(false); }
            }
        })();
        return () => { cancelled = true; };
    }, [open, kind, appointmentId, facilityCode, carrierCode, getToken]);

    /**
     * ⚠️ DEBOUNCED. The note is part of the message, so the preview has to
     * re-render as it is typed — but not once per keystroke.
     */
    useEffect(() => {
        if (!open || !details) return;
        const t = window.setTimeout(() => { void load(note); }, 350);
        return () => window.clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [note]);

    const toggle = (phone: string) => {
        setChosen(prev => {
            const next = new Set(prev);
            if (next.has(phone)) next.delete(phone); else next.add(phone);
            return next;
        });
    };

    /**
     * ⛔ THE HARD CONSTRAINT: NO REMEMBERED POSITION, NO AUTOMATIC STAMP.
     *
     * #110's whole premise is Pete's — *"the docs will be different for each
     * carrier"* — which is why the stamp is DRAGGED into place once and the
     * position remembered per carrier. A checkbox has no drag, so the automatic
     * path can only reuse a position a human already chose for this carrier.
     * Stamping a carrier we have never signed for would put ink at 62%/88% of a
     * page nobody has looked at, straight over whatever is there.
     */
    const signablePlacement = parsePlacement(placement);
    const canAutoSign =
        kind === 'paperwork' && signEnabled
        && (unsigned?.length ?? 0) > 0 && signablePlacement !== null;

    /** Why the checkbox is off, in words a scheduler can act on. */
    const autoSignBlockedBecause: string | null =
        kind !== 'paperwork' ? 'not applicable'
        : unsigned === null ? 'Checking what is on file…'
        : !signEnabled ? 'The signature stamp is switched off for this facility (Settings → Documents).'
        : unsigned.length === 0 ? 'Nothing on this appointment is waiting to be signed.'
        : signablePlacement === null
            ? 'No saved stamp position for this carrier yet. Sign one by hand from '
              + 'Paperwork first — the position is remembered and this becomes available.'
        : null;

    async function send() {
        if (!appointmentId || chosen.size === 0) return;
        setBusy(true);
        setError(null);
        setPostSend(null);
        try {
            const token = await getToken();

            // ⛔ THE TEXT GOES FIRST, ALWAYS. It is what the scheduler pressed the
            // button for; the stamp is an extra. Signing first would mean a failure
            // to stamp could stop a waiting driver being told his papers are ready.
            const res = await sendDoorText(token, appointmentId, [...chosen], note || null, kind);
            onSent?.({ sent: res.sent, optedOut: res.optedOut, failed: res.failed, doorCode: res.doorCode });

            if (!(autoSign && canAutoSign)) { onClose(); return; }

            // ---- the stamp ------------------------------------------------
            const place = parsePlacement(placement);
            if (!place) {
                // Should be unreachable — canAutoSign requires a parseable one.
                setPostSend('The driver was texted. The stamp position on file could not '
                    + 'be read, so nothing was signed — sign it by hand from Paperwork.');
                return;
            }

            const signedIds: string[] = [];
            const failures: string[] = [];
            for (const d of unsigned ?? []) {
                try {
                    const r = await signDocumentApi(token, d.documentId, place);
                    signedIds.push(r.signed.documentId);
                } catch (err) {
                    failures.push(`${d.fileName}: ${err instanceof ApiCallError ? err.details.error : 'failed'}`);
                }
            }

            // ⭐ "PRINT" IS OPENING THE PDF, which is what Pete settled on for #110
            // (*"Opening the pdf and printing is fine"*) — the browser cannot drive
            // the mapped printer directly, and pretending otherwise would silently
            // do nothing.
            if (autoPrint) {
                for (const id of signedIds) {
                    try {
                        const { url } = await getDocumentUrl(token, id);
                        window.open(url, '_blank', 'noopener,noreferrer');
                    } catch {
                        failures.push('the signed copy could not be opened for printing');
                    }
                }
            }

            if (failures.length > 0) {
                // ⚠️ SAYS THE TEXT WENT. Reporting only the stamp failure would have
                // a scheduler text the driver a second time.
                setPostSend(`The driver was texted. ${signedIds.length} signed, but: `
                    + failures.join('; '));
                return;
            }
            onClose();
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'Could not send.');
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>
                        {kind === 'paperwork'
                            ? 'Tell the driver his paperwork is ready'
                            : kind === 'work'
                                /**
                                 * ⭐ THE TITLE DOES NOT NAME THE DIRECTION, deliberately.
                                 * The dialog does not know it — the SERVER picks the word
                                 * from the load's direction — and a title that guessed
                                 * "unloading" on an outbound would contradict the preview
                                 * two inches below it. ⚠️ The preview is the source of
                                 * truth on this screen and it shows the real sentence.
                                 */
                                ? 'Tell the driver work has started on his truck'
                                /**
                                 * ⛔⛔ THE TITLE FOLLOWS THE MESSAGE, BECAUSE THIS DIALOG
                                 * OPENS BY ITSELF (Pete, 2026-09-13).
                                 *
                                 * `Calendar.tsx` opens it automatically after an arrival is
                                 * attached — on create-from-card and on attach-from-panel —
                                 * and a walk-up lands in the unassigned queue, so **the
                                 * commonest automatic open is the one with NO DOOR.**
                                 *
                                 * ⚠️ Titled *"Text the driver his dock door"* it then
                                 * announced itself and immediately contradicted itself with
                                 * *"no door assigned yet"*. ⛔ **A dialog that opens itself
                                 * and looks wrong teaches a scheduler to dismiss it — and
                                 * then it gets dismissed on the day there IS a door.**
                                 *
                                 * ⭐ The MESSAGE was always right: *"No dock door yet —
                                 * please wait in the lot, we will text it to you"* is
                                 * exactly what a driver at the gate wants. Only the heading
                                 * described the other case.
                                 */
                                : details && !details.doorCode
                                    ? 'Tell the driver to wait'
                                    : 'Text the driver his dock door'}
                    </DialogTitle>
                    <DialogDescription>
                        {details?.appointmentNumber ?? 'This appointment'}
                        {kind === 'door' && details && (details.doorCode
                            ? <span className="ml-1 font-medium text-emerald-700">· Door {details.doorCode}</span>
                            // ⚠️ SAID BEFORE THEY PRESS SEND, not discovered in the
                            // preview. Sending the "please wait" version is a
                            // deliberate act, and it commits somebody to sending a
                            // second text later.
                            : <span className="ml-1 font-medium text-amber-700">· no door assigned yet</span>)}
                        {kind === 'work' && (
                            /**
                             * ⛔ THE SAME ASSERTION THE PAPERWORK TEXT MAKES, and it is
                             * worth the same warning. Nothing here knows a forklift has
                             * actually started — a human is deciding that by looking at
                             * the dock. ⚠️ A driver told to stay with his vehicle for
                             * work that has not begun ignores the next one of these.
                             */
                            <span className="ml-1 text-muted-foreground">
                                · you are telling him work has begun on his truck
                            </span>
                        )}
                        {kind === 'paperwork' && (
                            // ⛔ THE ASSERTION BEING MADE, said out loud. Nothing in
                            // the system knows the truck is finished — a human is
                            // deciding that by looking at it, and a driver who walks
                            // to an office for papers that are not ready ignores the
                            // next one of these.
                            <span className="ml-1 text-muted-foreground">
                                · you are telling him the work is done and the papers are signed
                            </span>
                        )}
                    </DialogDescription>
                </DialogHeader>

                {loading && !details && <div className="text-xs text-muted-foreground">Loading…</div>}

                {details && (
                    <div className="space-y-3">
                        {/* ⛔ #163 — THE DUPLICATE THIS DIALOG WOULD OTHERWISE CREATE.
                            Automation texts an arrived driver when his door is assigned.
                            A scheduler who does not know that presses this button and the
                            driver gets two texts — the dedup window is only 2 minutes,
                            and deliberately so, because a genuine re-send is a real need
                            on a dock ("he says he never got it").
                            ⭐ So: tell the human what the machine already did. Read from
                            the audit, matched on THIS door, so it cannot disagree with
                            what actually happened. */}
                        {details.kind === 'door' && details.autoTextedThisDoorUtc && (
                            <div className="rounded border border-amber-400 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
                                <span className="font-medium">Already sent automatically.</span>{' '}
                                The system texted this driver about{' '}
                                {details.doorCode} at{' '}
                                {new Date(details.autoTextedThisDoorUtc).toLocaleTimeString([], {
                                    hour: 'numeric', minute: '2-digit',
                                })}
                                . Sending again is fine if he says he never got it — otherwise
                                he will receive a second copy.
                            </div>
                        )}

                        {/* ---- recipients ------------------------------------ */}
                        {details.recipients.length === 0 ? (
                            <div className="rounded border border-amber-400 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
                                There is no phone number on this appointment or its arrival, so
                                there is nobody to text. Add a driver, or edit the arrival to
                                add the number he checked in with.
                            </div>
                        ) : (
                            <div className="space-y-1">
                                <Label>Send to</Label>
                                {details.recipients.map(r => (
                                    <label
                                        key={r.phone}
                                        className="flex cursor-pointer items-center gap-2 rounded border px-2 py-1 text-xs"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={chosen.has(r.phone)}
                                            disabled={busy}
                                            onChange={() => toggle(r.phone)}
                                        />
                                        <span className="font-medium">{r.name || 'Driver'}</span>
                                        <span className="font-mono text-muted-foreground">{r.phone}</span>
                                        {/* ⭐ The label a scheduler needs to spot a
                                            substitute driver at a glance. */}
                                        <span className={`ml-auto rounded px-1 text-[10px] ${
                                            r.source === 'checkin'
                                                ? 'bg-emerald-100 text-emerald-800'
                                                : 'bg-slate-100 text-slate-700'}`}>
                                            {r.source === 'checkin'
                                                ? (r.alsoBooked ? 'checked in' : 'checked in — not on the booking')
                                                : 'on the booking'}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        )}

                        {/* ---- note ------------------------------------------- */}
                        <div className="space-y-1">
                            <Label htmlFor="door-note">Add a line (optional)</Label>
                            <Input
                                id="door-note"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                maxLength={160}
                                placeholder="e.g. Check in with the guard shack first"
                                disabled={busy}
                            />
                        </div>

                        {/* ---- the message ------------------------------------ */}
                        <div className="space-y-1">
                            <Label>What he will get</Label>
                            <pre className="whitespace-pre-wrap rounded border bg-muted/50 px-2 py-1.5 font-sans text-xs">
{details.message}
                            </pre>
                            {/* ⚠️ ONLY SHOWN WHEN IT COSTS MORE THAN ONE, rather than a
                                permanent counter — a number that is always there is a
                                number nobody reads. */}
                            {/**
                              * ⛔⛔ THE WORDING CHANGED ON 2026-08-27 BECAUSE IT WAS WRONG.
                              *
                              * Pete, looking at a live send: *"this is coming across
                              * everyone's phone as 1 text. Sure the message is correct?"*
                              *
                              * ⭐ HE IS RIGHT ABOUT THE PHONE AND THE COUNT IS ALSO RIGHT.
                              * A long SMS is split at the network and REASSEMBLED by the
                              * handset — concatenated SMS, a UDH header on each part — so
                              * it shows as one message. That is the normal outcome, not the
                              * exception.
                              *
                              * ⛔ SO "it may arrive in pieces" WAS THE FAILURE. It described
                              * the rare case as the expected one, and the screen therefore
                              * disagreed with the phone in Pete's hand — which is the fastest
                              * way to teach somebody to ignore a warning. ⚠️ What is actually
                              * true every time is the BILL: two segments are charged.
                              *
                              * ⚠️ THE SPLIT IS STILL WORTH A WORD, because it is real on old
                              * handsets and on some international routes — but as the caveat
                              * it is, not as the headline.
                              */}
                            {details.segments > 1 && (
                                <div className="text-[11px] text-amber-700">
                                    Charged as {details.segments} text messages
                                    {details.unicode && ' (a special character has halved the limit)'}.
                                    It arrives on his phone as one — older handsets can show it
                                    split. Shortening the extra line keeps it to a single message.
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {error && (
                    <div className="rounded border border-destructive/40 bg-destructive/5 px-2 py-1 text-xs text-destructive">
                        {error}
                    </div>
                )}

                {/* ⭐ #146 — sign, timestamp and print at the moment the driver is
                    told. Paperwork only: there is nothing to sign when the message
                    is "your door is 12". */}
                {kind === 'paperwork' && (
                    <div className="space-y-2 rounded-md border p-3">
                        <label className={`flex items-start gap-2 text-sm ${canAutoSign ? 'cursor-pointer' : 'opacity-70'}`}>
                            <input
                                type="checkbox"
                                className="mt-0.5"
                                checked={autoSign && canAutoSign}
                                disabled={!canAutoSign || busy}
                                onChange={(e) => {
                                    setAutoSign(e.target.checked);
                                    // Printing an unsigned copy is not the ask, so
                                    // untick it together rather than leaving a
                                    // checkbox on that now means nothing.
                                    if (!e.target.checked) setAutoPrint(false);
                                }}
                            />
                            <span>
                                <span className="font-medium">Sign and timestamp the paperwork</span>
                                {canAutoSign && unsigned && (
                                    <span className="text-muted-foreground">
                                        {' '}— {unsigned.length} document{unsigned.length === 1 ? '' : 's'},
                                        {' '}using this carrier's saved stamp position
                                    </span>
                                )}
                                {/* ⚠️ THE REASON, not just a disabled box. "Why can I
                                    not tick this?" has four different answers and
                                    only one of them is something to fix. */}
                                {!canAutoSign && autoSignBlockedBecause && autoSignBlockedBecause !== 'not applicable' && (
                                    <span className="block text-xs text-muted-foreground">
                                        {autoSignBlockedBecause}
                                    </span>
                                )}
                            </span>
                        </label>

                        <label className={`flex items-start gap-2 text-sm ${autoSign && canAutoSign ? 'cursor-pointer' : 'opacity-70'}`}>
                            <input
                                type="checkbox"
                                className="mt-0.5"
                                checked={autoPrint}
                                disabled={!autoSign || !canAutoSign || busy}
                                onChange={(e) => setAutoPrint(e.target.checked)}
                            />
                            <span>
                                <span className="font-medium">Open the signed copy to print</span>
                                <span className="block text-xs text-muted-foreground">
                                    {/* Settled in #110: the browser cannot drive the
                                        mapped printer, so "print" is opening the PDF. */}
                                    Opens each signed PDF in a new tab — print from there
                                    to the scanner/printer.
                                </span>
                            </span>
                        </label>
                    </div>
                )}

                {/* The text went; the stamp did not. Two outcomes, said separately. */}
                {postSend && (
                    <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
                        {postSend}
                    </div>
                )}

                <DialogFooter>
                    <div className="flex w-full justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
                            Cancel
                        </Button>
                        <Button size="sm" onClick={() => void send()} disabled={busy || chosen.size === 0}>
                            {busy
                                ? 'Sending…'
                                : chosen.size > 1
                                    ? `Text ${chosen.size} drivers`
                                    : kind === 'paperwork' ? 'Send it' : 'Send the text'}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
